import { Fragment, useSyncExternalStore, type ReactNode } from "react";
import { WindowTaskbar } from "./WindowTaskbar";
import type { WindowEntry, WindowStackManager } from "./windowStackManager";

type WindowStackHostProps = {
  stack: WindowStackManager;
  // kind 별로 콘텐츠를 렌더한다. 호스트는 kind 를 모르고 소비처가 분기한다. active 면 전체화면, 아니면 최소화다.
  renderWindow: (entry: WindowEntry, active: boolean) => ReactNode;
};

// 전체화면 창 스택 호스트다. 모든 엔트리를 항상 마운트해 콘텐츠 상태를 보존하고, 활성 창 하나만
// 전체화면으로 표시한다. 창이 하나라도 있으면 활성 창이 있어도 상단 작업표시줄을 항상 표시해 열린
// 창 목록을 보여 준다. 이 호스트는 스택을 구독만 하고 콘텐츠/도메인은 모른다.
export function WindowStackHost({ stack, renderWindow }: WindowStackHostProps): ReactNode {
  const { entries, activeId } = useSyncExternalStore(stack.subscribe, stack.getSnapshot);

  // 작업표시줄 본문 클릭이다. 활성 항목이면 최소화(배경으로 토글)한다. 아니면 그 창을 전체화면으로 전환한다.
  const handleSelect = (id: string): void => {
    if (id === activeId) {
      stack.minimize(id);
    } else {
      stack.setActive(id);
    }
  };

  return (
    <>
      {entries.map((entry) => (
        // entry.id 를 key 로 준다. 그래서 창마다 콘텐츠를 따로 만들고, 최소화해도 mount 가 유지된다.
        <Fragment key={entry.id}>{renderWindow(entry, entry.id === activeId)}</Fragment>
      ))}
      {entries.length > 0 ? (
        <WindowTaskbar
          entries={entries}
          activeId={activeId}
          onSelect={handleSelect}
          onClose={stack.close}
        />
      ) : null}
    </>
  );
}
